import posthog from 'posthog-js';

class PostHogService {
  private initialized = false;

  /**
   * Initialize PostHog with environment configuration
   */
  initialize(): void {
    const apiKey = process.env.REACT_APP_POSTHOG_API_KEY;
    const host = process.env.REACT_APP_POSTHOG_HOST;

    if (!apiKey) {
      console.warn('PostHog API key not configured');
      return;
    }

    if (this.initialized) {
      console.warn('PostHog already initialized');
      return;
    }

    try {
      posthog.init(apiKey, {
        api_host: host ?? 'https://app.posthog.com',
        loaded: () => {
          if (process.env.NODE_ENV === 'development') {
            console.log('PostHog initialized successfully');
          }
        },
        capture_pageview: false,
        capture_pageleave: true,
        persistence: 'localStorage+cookie',
        person_profiles: 'identified_only',
      });

      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
    }
  }

  /**
   * Identify a user with PostHog
   */
  identify(userId: string, properties?: Record<string, any>): void {
    if (!this.initialized) {
      console.warn('PostHog not initialized');
      return;
    }

    try {
      posthog.identify(userId, properties);
    } catch (error) {
      console.error('Failed to identify user in PostHog:', error);
    }
  }

  /**
   * Track a custom event
   */
  capture(eventName: string, properties?: Record<string, any>): void {
    if (!this.initialized) {
      console.warn('PostHog not initialized');
      return;
    }

    try {
      posthog.capture(eventName, properties);
    } catch (error) {
      console.error('Failed to capture event in PostHog:', error);
    }
  }

  /**
   * Reset the user (e.g., on logout)
   */
  reset(): void {
    if (!this.initialized) {
      return;
    }

    try {
      posthog.reset();
    } catch (error) {
      console.error('Failed to reset PostHog:', error);
    }
  }

  /**
   * Set user properties
   */
  setPersonProperties(properties: Record<string, any>): void {
    if (!this.initialized) {
      console.warn('PostHog not initialized');
      return;
    }

    try {
      posthog.people.set(properties);
    } catch (error) {
      console.error('Failed to set person properties in PostHog:', error);
    }
  }

  /**
   * Check if a feature flag is enabled
   */
  isFeatureEnabled(flagKey: string, defaultValue: boolean = false): boolean {
    if (!this.initialized) {
      return defaultValue;
    }

    try {
      return posthog.isFeatureEnabled(flagKey) ?? defaultValue;
    } catch (error) {
      console.error('Failed to check feature flag in PostHog:', error);
      return defaultValue;
    }
  }

  /**
   * Get the PostHog instance for advanced usage
   */
  getInstance() {
    return posthog;
  }
}

export const posthogService = new PostHogService();
